import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MdSend } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { scheduleCampaignRequest, scheduleCampaignReset } from '../../redux/slices/scheduleCampaignSlice';
import PlatformSelector from './PlatformSelector';
import TimeDropdown from './TimeDropdown';
import type { ScheduleFormProps, SocialMedium, ScheduleData } from './types';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';

const ScheduleForm: React.FC<ScheduleFormProps> = ({
  flyerImageUrl,
  generatedContentId,
  flyerId,
  socialMediaData = [],
  restaurantId,
  onSubmit
}) => {
  const dispatch = useAppDispatch();
  const { navigate } = useRestaurantNav();
  const scheduleCampaignState = useAppSelector((state) => state.scheduleCampaign);
  const [formData, setFormData] = useState<ScheduleData>({
    fromDate: '',
    toDate: '',
    time: '',
    selectedMediums: []
  });

  const handlePlatformToggle = (platform: SocialMedium) => {
    setFormData(prev => ({
      ...prev,
      selectedMediums: prev.selectedMediums.includes(platform)
        ? prev.selectedMediums.filter(m => m !== platform)
        : [...prev.selectedMediums, platform]
    }));
  };

  // Handle Schedule Campaign success
  useEffect(() => {
    if (scheduleCampaignState.success) {
      console.log('Campaign scheduled successfully!');
      dispatch(scheduleCampaignReset());
      navigate(`/${restaurantId}/marketing-calendar`);
      onSubmit(formData);
    }
  }, [scheduleCampaignState.success, dispatch, navigate, restaurantId, onSubmit, formData]);

  // Handle errors
  useEffect(() => {
    if (scheduleCampaignState.error) {
      console.error('Schedule campaign error:', scheduleCampaignState.error);
    }
  }, [scheduleCampaignState.error]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Scheduling campaign:', formData);
    
    if (formData.fromDate && formData.time && formData.selectedMediums.length > 0) {
      // Convert date + time to Unix timestamp
      const dateTimeString = `${formData.fromDate}T${formData.time}:00`;
      const scheduledDate = Math.floor(new Date(dateTimeString).getTime() / 1000).toString();

      // Dispatch the Redux action
      dispatch(scheduleCampaignRequest({
        start_date: formData.fromDate,
        end_date: formData.toDate || formData.fromDate,
        generated_content_id: generatedContentId,
        social_media: JSON.stringify(formData.selectedMediums.map(m => m.toUpperCase())),
        scheduled_date: scheduledDate,
        restaurant_id: restaurantId,
        flyer_id: flyerId,
      }));
    }
  };

  const isFormValid = formData.fromDate && formData.time && formData.selectedMediums.length > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Side: Flyer Preview */}
        <div className="lg:col-span-1">
          <h3 className="text-sm font-semibold text-gray-400 mb-3">Flyer Preview</h3>
          <div className="relative rounded-xl overflow-hidden shadow-lg border border-gray-700">
            <img
              src={flyerImageUrl}
              alt="Flyer Preview"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>

        {/* Right Side: Form Fields */}
        <div className="lg:col-span-2 space-y-6">
          {/* Date & Time Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Schedule Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* From Date */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  From Date
                </label>
                <input
                  type="date"
                  value={formData.fromDate}
                  onChange={(e) => setFormData({ ...formData, fromDate: e.target.value })}
                  className="w-full bg-[#212121] text-white rounded-lg px-4 py-3 border border-gray-600 focus:border-[#EE4423] focus:outline-none focus:ring-1 focus:ring-[#EE4423] transition-colors"
                  required
                />
              </div>

              {/* To Date */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  To Date (Optional)
                </label>
                <input
                  type="date"
                  value={formData.toDate}
                  onChange={(e) => setFormData({ ...formData, toDate: e.target.value })}
                  min={formData.fromDate}
                  className="w-full bg-[#212121] text-white rounded-lg px-4 py-3 border border-gray-600 focus:border-[#EE4423] focus:outline-none focus:ring-1 focus:ring-[#EE4423] transition-colors"
                />
              </div>

              {/* Time Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Time
                </label>
                <TimeDropdown
                  value={formData.time}
                  onChange={(value) => setFormData({ ...formData, time: value })}
                  required
                />
              </div>
            </div>
          </div>

          {/* Social Media Selection */}
          <PlatformSelector
            selectedPlatforms={formData.selectedMediums}
            onToggle={handlePlatformToggle}
          />

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <motion.button
              type="submit"
              whileHover={{ scale: (isFormValid && !scheduleCampaignState.loading) ? 1.02 : 1 }}
              whileTap={{ scale: (isFormValid && !scheduleCampaignState.loading) ? 0.98 : 1 }}
              disabled={!isFormValid || scheduleCampaignState.loading}
              className={`px-6 py-2.5 text-sm font-semibold rounded-full flex items-center justify-center gap-2 transition-colors ${
                (isFormValid && !scheduleCampaignState.loading)
                  ? 'bg-[#EE4423] hover:bg-[#d63d20] text-white cursor-pointer'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {scheduleCampaignState.loading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Scheduling...</span>
                </>
              ) : (
                <>
                  <MdSend size={18} />
                  Schedule Campaign
                </>
              )}
            </motion.button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ScheduleForm;
